import styled from '@emotion/styled';
import type { AccountTrialInfo } from '@hotjar/account';
import { Alert, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import {
  getTrialRemainingDays,
  isOnActiveTrial,
  isOnEligibleTrial,
} from '~/subscription-management/utils/trial/trial.utils';
import { ChurnFooter } from './components/ChurnFooter';
import { CHECKOUT_FOOTER_CTA, trackClick } from '~/subscription-management/utils/tracking';
import { ManageSubscriptionsFooter } from './components/ManageSubscriptionsFooter';
import { InvoiceDetailsFooter } from './components/InvoiceDetailsFooter';
import { ReviewOrderFooter } from './components/ReviewOrderFooter';
import type {
  Context,
  States,
  Event,
} from '~/subscription-management/checkout/state-machine/checkout.machine.types';

export const S = {
  Footer: styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${tokens.spacer.x2};
    padding: ${tokens.spacer.x2} ${tokens.spacer.x3} ${tokens.spacer.x3};
    background: ${tokens.color.ui_background};
  `,
  Alert: styled(Alert)`
    width: 480px;
    white-space: pre-line;
    margin-right: ${tokens.spacer.x5};
  `,
};

interface Props {
  context: Context;
  matchState(state: Extract<States, string>): boolean;
  send: (event: Event) => void;
  isLoading: boolean;
  isDisabled: boolean;
  trialInfo: AccountTrialInfo | null;
  restoreFocus: () => void;
}

export function Footer({
  context,
  matchState,
  send,
  isLoading,
  trialInfo,
  isDisabled,
  restoreFocus,
}: Props): JSX.Element {
  const { accountId, isChurning, isOnTrialWithBasicAllowances, canProceed, completedSteps } =
    context;

  const { t: tFooter } = useTranslationWithPrefix('checkout:footer');

  const getOnTrialAlertMessage = (): string => {
    if (isOnActiveTrial(trialInfo)) {
      return tFooter('active-trial-with-basic-allowances-selected', {
        days: getTrialRemainingDays(trialInfo),
      });
    }

    if (isOnEligibleTrial(trialInfo)) {
      return tFooter('eligible-trial-with-basic-allowances-selected');
    }

    return '';
  };

  const handleClick = (eventName: string, skip = false) => {
    trackClick({ eventName, accountId, uiPosition: 'Checkout Footer' });
    send({ type: 'NEXT', skip });
    restoreFocus();
  };

  const displayAsDisabled = isDisabled || (!isLoading && !canProceed);

  return (
    <S.Footer>
      {isOnTrialWithBasicAllowances && <S.Alert>{getOnTrialAlertMessage()}</S.Alert>}

      {matchState('editOrder.steps.editSubscription') && (
        <ManageSubscriptionsFooter
          onClick={() => handleClick(CHECKOUT_FOOTER_CTA.continueToCheckout)}
          isDisabled={displayAsDisabled}
          isLoading={isLoading}
        />
      )}

      {matchState('editOrder.steps.invoiceDetails') && (
        <InvoiceDetailsFooter
          onClickSkip={() => handleClick(CHECKOUT_FOOTER_CTA.invoiceDetailsSecondary, true)}
          accountId={accountId}
          isDisabled={displayAsDisabled}
          isLoading={isLoading}
          skipPaymentDetails={completedSteps.includes('editOrder.steps.paymentDetails')}
          hasErrored={matchState('editOrder.steps.invoiceDetails.updateError')}
        />
      )}

      {matchState('editOrder.steps.reviewOrder') &&
        (isChurning ? (
          <ChurnFooter
            context={context}
            send={send}
            isDisabled={isDisabled}
            isLoading={isLoading}
          />
        ) : (
          <ReviewOrderFooter
            onClick={() => handleClick(CHECKOUT_FOOTER_CTA.reviewOrder)}
            isDisabled={displayAsDisabled}
            isLoading={isLoading}
          />
        ))}
    </S.Footer>
  );
}
